// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { useState } from "react";
import { LuThumbsUp, LuThumbsDown } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { showHelpDoc, showTour, invokeError } from "../../store/auth/action";
import { BookOpenIcon, FerrisWheelIcon, TriangleAlertIcon, TicketIcon, ChevronRightIcon, CircleCheckBigIcon } from "../icons";
import { submitFeedBack } from "../../services/feedback.service";
import { getSession } from "../../utils/sessionstore";

const HelpAndSupportRow = ({ icon, title, subtitle, onClick, onMouseEnter }) => (
  <div
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    className="self-stretch h-14 shrink-0 px-2.5 py-2 bg-[#FAFAFA] hover:bg-general-accent rounded-lg outline outline-1 outline-offset-[-1px] outline-[#E5E5E5] inline-flex justify-start items-center gap-2.5 cursor-pointer ani"
  >
    <span className="size-4 shrink-0 flex justify-center items-center text-general-foreground">
      {icon}
    </span>
    <div className="flex-1 min-w-0 inline-flex flex-col justify-center items-start gap-0.5">
      <div className="self-stretch text-left truncate text-general-foreground text-xs font-medium leading-4">
        {title}
      </div>
      {subtitle && (
        <div className="self-stretch text-left truncate text-general-muted-foreground text-[11px] font-normal leading-3.5">
          {subtitle}
        </div>
      )}
    </div>
    <span className="size-4 shrink-0 flex justify-center items-center text-general-muted-foreground">
      <ChevronRightIcon className="size-3.5" />
    </span>
  </div>
);

const HelpAndSupportContent = ({ onNavigate, onRaiseTicket, onMyTickets, onHelpGuide, onHelpGuideHover }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientID } = useParams();
  const { activedashboard, userDetails, clientname } = useSelector((state) => ({
    activedashboard: state.activedashboard,
    userDetails: state.userDetails,
    clientname: state.clientname,
  }));

  // Help Guide is tied to a report's content, so it only applies on dashboard
  // pages, and only when that dashboard actually has a guide attached - the same
  // condition useHelpGuide's isAvailable gates openHelpGuide on, so the row is
  // never shown when clicking it would do nothing. The tour walks through the
  // Landing Page, so it only belongs there.
  const currentPath = (clientID ? location.pathname.replace(new RegExp(`^/${clientID}`), "") : location.pathname) || "/";
  const showHelpGuide = currentPath.startsWith("/dashboard") && activedashboard?.helpDoc?.length > 0;
  const showTakeATour = currentPath === "/";
  // "Liking this Page?" feedback doesn't make sense on the Profile page (account
  // settings aren't really something to rate) - hidden there only, still shown
  // everywhere else.
  const showFeedback = !currentPath.startsWith("/profile");

  const [feedback, setFeedback] = useState(null);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const goToTicket = () => {
    onNavigate?.();
    navigate(clientID ? `/${clientID}/ticket` : "/ticket");
  };

  const submitFeedback = async (answer, commentText = "") => {
    if (submitting) return;
    setFeedback(answer);
    setSubmitting(true);
    try {
      await submitFeedBack({
        token: getSession("session_hwai"),
        userId: userDetails?.userId,
        recommendation: commentText,
        fields: [
          {
            name: activedashboard?.dashboardname || activedashboard?.label || "Dashboard",
            stars: answer === "up" ? 1 : 0,
          },
        ],
        config: {
          feedback: answer,
          dashboardId: activedashboard?._id,
          dashboardName: activedashboard?.dashboardname || activedashboard?.label,
          clientId: clientID || getSession("client_id"),
          clientName: clientname,
        },
      });
      setSubmitted(true);
      setComment("");
    } catch (err) {
      dispatch(invokeError(err?.message || "Couldn't submit feedback, please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="self-stretch flex-1 p-3 flex flex-col justify-start items-start gap-2.5">
        {showHelpGuide && (
          <HelpAndSupportRow
            icon={<BookOpenIcon className="size-4" />}
            title="Help Guide"
            onMouseEnter={onHelpGuideHover}
            onClick={() => {
              if (onHelpGuide) {
                onHelpGuide();
              } else {
                onNavigate?.();
                dispatch(showHelpDoc());
              }
            }}
          />
        )}
        {showTakeATour && (
          <HelpAndSupportRow
            icon={<FerrisWheelIcon className="size-4" />}
            title="Take a Tour"
            onClick={() => {
              onNavigate?.();
              dispatch(showTour(true));
            }}
          />
        )}
        <HelpAndSupportRow
          icon={<TriangleAlertIcon className="size-4" />}
          title="Raise a Ticket"          
          onClick={onRaiseTicket || goToTicket}
        />
        <HelpAndSupportRow
          icon={<TicketIcon className="size-4" />}
          title="My Tickets"
          onClick={onMyTickets || goToTicket}
        />
      </div>

      {showFeedback && (
      <>
      <div className="self-stretch h-px bg-general-border" />

      <div className="self-stretch px-3 py-4 bg-general-accent flex flex-col justify-center items-start gap-3">
        {submitted ? (
          <div className="self-stretch inline-flex justify-start items-center gap-2">
            <span className="text-general-secondary-foreground text-sm font-medium leading-5">
              Thanks, noted.
            </span>
            <CircleCheckBigIcon className="size-4 text-[#28A745]" />
          </div>
        ) : (
        <>
        <div className="self-stretch inline-flex justify-start items-center gap-2.5">
          <div className="flex-1 text-left text-general-secondary-foreground text-sm font-medium leading-5">
            Liking this Page?
          </div>
          <div className="flex justify-start items-center gap-2">
            <button
              type="button"
              onClick={() => submitFeedback("up")}
              disabled={submitting}
              className={`group min-w-8 min-h-8 p-1.5 bg-general-input rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-general-border hover:outline-[#E5E5E5] flex justify-center items-center ani disabled:opacity-60 ${feedback === "down" ? "opacity-40" : ""}`}
            >
              <LuThumbsUp className={`size-3.5 ani ${feedback === "up" ? "text-[#EAB308]" : "text-general-foreground group-hover:text-[#EAB308]"}`} />
            </button>
            <button
              type="button"
              onClick={() => setFeedback(feedback === "down" ? null : "down")}
              disabled={submitting}
              className={`group min-w-8 min-h-8 p-1.5 bg-general-input rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-general-border hover:outline-[#E5E5E5] flex justify-center items-center ani disabled:opacity-60 ${feedback === "up" ? "opacity-40" : ""}`}
            >
              <LuThumbsDown className={`size-3.5 ani ${feedback === "down" ? "text-[#EAB308]" : "text-general-foreground group-hover:text-[#EAB308]"}`} />
            </button>
          </div>
        </div>

        {feedback === "down" && (
          <>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What's not working? (optional)"
              className="self-stretch h-20 p-2 bg-general-input rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-general-border text-general-foreground placeholder:text-general-muted-foreground text-sm font-normal leading-5 resize-none focus:outline-general-primary"
            />
            <div className="self-stretch flex flex-col justify-center items-end">
              <button
                type="button"
                onClick={() => submitFeedback("down", comment)}
                disabled={submitting}
                className="self-stretch min-h-9 px-4 py-2 bg-general-primary rounded-lg flex justify-center items-center gap-2 hover:opacity-90 ani disabled:opacity-60"
              >
                <span className="text-center text-general-primary-foreground text-sm font-medium leading-5">
                  {submitting ? "Submitting..." : "Submit"}
                </span>
              </button>
            </div>
          </>
        )}
        </>
        )}
      </div>
      </>
      )}
    </>
  );
};

export default HelpAndSupportContent;
